import { computed, type MaybeRef, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";

interface UseAdminOrganizationWalletOptions {
  organizationId: MaybeRef<number | null | undefined>;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function useAdminOrganizationWallet(options: UseAdminOrganizationWalletOptions) {
  const api = useApi();
  const organizationId = computed(() => unref(options.organizationId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(organizationId.value));

  const query = useQuery({
    queryKey: computed(() => ["admin", "wallet", "organization", organizationId.value]),
    queryFn: async () => {
      if (!isPositiveId(organizationId.value)) {
        throw new Error("organizationId is required to load organization wallet");
      }

      return await api.admin.wallet.getOrganizationWallet({
        params: { organization: organizationId.value },
      });
    },
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const wallet = computed(() => query.data.value?.wallet ?? null);

  return {
    wallet,
    availableBalanceCents: computed(() => wallet.value?.available_balance_cents ?? 0),
    holdBalanceCents: computed(() => wallet.value?.hold_balance_cents ?? 0),
    lifetimeGrossCents: computed(() => wallet.value?.lifetime_gross_cents ?? 0),
    lifetimeNetCents: computed(() => wallet.value?.lifetime_net_cents ?? 0),
    currency: computed(() => wallet.value?.currency ?? null),
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    query,
  };
}
